import { useEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { UpOutlined } from '@ant-design/icons';
import { useMapColor } from '../../../hooks/SimViewer/useMapColor';
import type { Legend } from '../../../types/common';
import { scrollbarStyle } from '../../../styles/scrollbar';
import { ResultSection } from './ResultSection/ResultSection';
import { MapAppearanceSection } from './MapAppearanceSection';
import { CustomAreaSection } from './CustomArea/CustomAreaSection';

type Props = {
  legends: Record<string, Legend[]>;
};

const SimViewerTools = ({ legends }: Props): ReactNode => {
  const { mapColor } = useMapColor();

  const [isResultSectionOpen, setIsResultSectionOpen] = useState(true);
  const [isMapAppearanceSectionOpen, setIsMapAppearanceSectionOpen] =
    useState(true);
  const [isCustomAreaSectionOpen, setIsCustomAreaSectionOpen] = useState(true);

  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = (): void => {
    const container = scrollContainerRef.current;
    if (container) {
      setIsAtTop(container.scrollTop < 15);
      setIsAtBottom(
        container.scrollHeight - container.clientHeight - container.scrollTop <
          15
      );
    }
  };

  useEffect(() => {
    handleScroll();
  }, []);

  return (
    <RightSideMenuContainer>
      <ScrollContainer
        isAtTop={isAtTop}
        isAtBottom={isAtBottom}
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        <MenuContentWrapper>
          <ResultSection
            legends={legends}
            menuOpen={isResultSectionOpen}
            toggleMenuOpen={(): void => {
              setIsResultSectionOpen((currentState: boolean): boolean => {
                return !currentState;
              });
            }}
          />
          <MapAppearanceSection
            menuOpen={isMapAppearanceSectionOpen}
            toggleMenuOpen={(): void => {
              setIsMapAppearanceSectionOpen(
                (currentState: boolean): boolean => {
                  return !currentState;
                }
              );
            }}
          />
          <CustomAreaSection
            menuOpen={isCustomAreaSectionOpen}
            toggleMenuOpen={(): void => {
              setIsCustomAreaSectionOpen((currentState: boolean): boolean => {
                return !currentState;
              });
            }}
          />
        </MenuContentWrapper>
      </ScrollContainer>
      {!isAtTop && (
        <ScrollArrowContainer mapColor={mapColor} side="top">
          <UpOutlined />
        </ScrollArrowContainer>
      )}
      {!isAtBottom && (
        <ScrollArrowContainer mapColor={mapColor} side="bottom">
          <UpOutlined rotate={180} />
        </ScrollArrowContainer>
      )}
    </RightSideMenuContainer>
  );
};

const RightSideMenuContainer = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  right: 0;
  padding: 16px;
  pointer-events: none;
  height: calc(100% - 16px);
`;

const MenuContentWrapper = styled.div`
  width: 249px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
  pointer-events: auto;
`;

const ScrollContainer = styled.div<{
  isAtTop: boolean;
  isAtBottom: boolean;
}>`
  height: 100%;
  overflow-y: auto;
  scrollbar-color: #41617d transparent;
  ${scrollbarStyle}

  mask-image: linear-gradient(
    to bottom,
    ${(props) => (props.isAtTop ? 'black' : 'transparent')} 0px,
    black 30px,
    black calc(100% - 30px),
    ${(props) => (props.isAtBottom ? 'black' : 'transparent')} 100%
  );
`;

const ScrollArrowContainer = styled.div<{
  mapColor: 'light' | 'dark';
  side: 'top' | 'bottom';
}>`
  position: absolute;
  ${(props) => (props.side === 'top' ? 'top: 0;' : 'bottom: 0;')}
  left: 50%;
  transform: translate(
    -50%,
    ${(props) => (props.side === 'top' ? '-' : '')}6px
  );
  display: flex;
  justify-content: center;
  color: ${(props) => (props.mapColor === 'dark' ? 'lightgray' : 'gray')};
`;

export { SimViewerTools };
